import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import { KeyRound, AlertCircle, CheckCircle, ArrowLeft } from 'lucide-react';

const ForgotPassword = ({ onBack, onSuccess }) => {
  const [step, setStep] = useState('username'); // 'username', 'recovery', 'success'
  const [username, setUsername] = useState('');
  const [recoveryPhrase, setRecoveryPhrase] = useState(Array(24).fill(''));
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleCheckUsername = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/check-recovery-available`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ username }),
      });

      const data = await response.json();

      if (data.recovery_available) {
        setStep('recovery');
      } else {
        setError("Ce compte n'a pas de système de récupération configuré. Veuillez contacter l'administrateur.");
      }
    } catch (err) {
      setError('Erreur lors de la vérification du compte.');
    }

    setLoading(false);
  };

  const handleRecoverySubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas.');
      setLoading(false);
      return;
    }

    if (newPassword.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères.');
      setLoading(false);
      return;
    }

    // Join recovery phrase
    const phrase = recoveryPhrase.join(' ').trim();

    if (recoveryPhrase.filter(w => w.trim()).length < 24) {
      setError('Veuillez saisir les 24 mots de récupération.');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/validate-recovery-phrase`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          username,
          recovery_phrase: phrase,
          new_password: newPassword,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess('Mot de passe réinitialisé avec succès ! Vous allez être redirigé...');
        setStep('success');
        
        // Cookie is set automatically by the browser
        setTimeout(() => {
          onSuccess();
        }, 2000);
      } else {
        setError(data.detail || 'Phrase de récupération incorrecte.');
      }
    } catch (err) {
      setError('Erreur lors de la réinitialisation du mot de passe.');
    }

    setLoading(false);
  };

  const handleWordChange = (index, value) => {
    const newWords = [...recoveryPhrase];
    newWords[index] = value.trim();
    setRecoveryPhrase(newWords);
  };

  const handlePasteInField = (e, index) => {
    e.stopPropagation(); // Empêcher la propagation vers le conteneur parent
    
    const pastedText = e.clipboardData.getData('text').trim();
    const words = pastedText.split(/\s+/);
    
    // Si plusieurs mots, remplir plusieurs champs
    if (words.length > 1) {
      e.preventDefault();
      const newWords = [...recoveryPhrase];
      words.forEach((word, i) => {
        if (index + i < 24) {
          newWords[index + i] = word.trim();
        }
      });
      setRecoveryPhrase(newWords);
      setError('');
    } else {
      // Si un seul mot, remplacer juste ce champ
      e.preventDefault();
      const newWords = [...recoveryPhrase];
      newWords[index] = pastedText;
      setRecoveryPhrase(newWords);
    }
  };

  const handlePasteAll = (e) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    const words = pastedText.trim().split(/\s+/);
    
    if (words.length === 24) {
      setRecoveryPhrase(words);
      setError(''); // Clear any previous errors
    } else if (words.length > 0) {
      // Fill as many fields as possible
      const newWords = [...recoveryPhrase];
      words.forEach((word, i) => {
        if (i < 24) {
          newWords[i] = word.trim();
        }
      });
      setRecoveryPhrase(newWords);
      setError(''); // Clear error
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden" 
         style={{
           background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
         }}>
      {/* Animated background */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-white/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-white/10 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
      </div>
      
      <Card className="w-full max-w-2xl relative z-10 shadow-2xl border-0" style={{
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(20px)',
      }}>
        <CardHeader className="text-center pb-4">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl"
               style={{
                 background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                 boxShadow: '0 10px 40px rgba(102, 126, 234, 0.4)',
               }}>
            <KeyRound className="h-8 w-8 text-white" />
          </div>
          <CardTitle className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
            Récupération de mot de passe
          </CardTitle>
          <CardDescription className="text-base mt-2">
            {step === 'username' && "Saisissez votre email"}
            {step === 'recovery' && "Saisissez votre phrase de récupération de 24 mots"}
            {step === 'success' && "Réinitialisation réussie !"}
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          {error && (
            <Alert className="mb-4 border-0 shadow-lg" style={{
              background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
            }}>
              <AlertCircle className="h-4 w-4 text-red-600" />
              <AlertDescription className="text-red-800 font-medium ml-2">
                {error}
              </AlertDescription>
            </Alert>
          )}

          {success && (
            <Alert className="mb-4 border-0 shadow-lg" style={{
              background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
            }}>
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800 font-medium ml-2">
                {success}
              </AlertDescription>
            </Alert>
          )}

          {step === 'username' && (
            <form onSubmit={handleCheckUsername} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="username">Email</Label>
                <Input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="h-12 border-2 border-gray-200 focus:border-indigo-500 rounded-xl"
                  placeholder="Votre email"
                  required
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  onClick={onBack}
                  variant="outline"
                  className="flex-1 h-12 rounded-xl"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Retour
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="flex-1 h-12 rounded-xl"
                  style={{
                    background: loading ? '#9ca3af' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  }}
                >
                  {loading ? 'Vérification...' : 'Continuer'}
                </Button>
              </div>
            </form>
          )}

          {step === 'recovery' && (
            <form onSubmit={handleRecoverySubmit} className="space-y-4">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Phrase de récupération (24 mots)</Label>
                  <span className="text-xs text-gray-500">Coller mot par mot ou tous en une fois</span>
                </div>
                
                <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto p-2 border-2 border-gray-200 rounded-xl"
                     onPaste={handlePasteAll}>
                  {recoveryPhrase.map((word, index) => (
                    <div key={`recovery-word-${index}`} className="flex items-center gap-2">
                      <span className="text-xs text-gray-500 w-8">{index + 1}.</span>
                      <Input
                        type="text"
                        value={word}
                        onChange={(e) => handleWordChange(index, e.target.value)}
                        onPaste={(e) => handlePasteInField(e, index)}
                        className="h-9 text-sm"
                        placeholder={`Mot ${index + 1}`}
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-3 pt-2">
                <div className="space-y-2">
                  <Label htmlFor="newPassword">Nouveau mot de passe</Label>
                  <Input
                    id="newPassword"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="h-12 border-2 border-gray-200 focus:border-indigo-500 rounded-xl"
                    placeholder="Au moins 8 caractères"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirmer le mot de passe</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="h-12 border-2 border-gray-200 focus:border-indigo-500 rounded-xl"
                    placeholder="Confirmer le mot de passe"
                    required
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  onClick={() => setStep('username')}
                  variant="outline"
                  className="flex-1 h-12 rounded-xl"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Retour
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="flex-1 h-12 rounded-xl"
                  style={{
                    background: loading ? '#9ca3af' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  }}
                >
                  {loading ? 'Réinitialisation...' : 'Réinitialiser le mot de passe'}
                </Button>
              </div>
            </form>
          )}

          {step === 'success' && (
            <div className="text-center py-8">
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <p className="text-lg text-gray-700">Redirection en cours...</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ForgotPassword;
